export const normalizeChartNumericValue = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export const getChartDatastreamDomain = (datastream) => {
  if (!datastream) return { min: null, max: null };

  return {
    min: normalizeChartNumericValue(
      datastream.min ?? datastream.minimum ?? datastream.minValue ?? datastream.min_value
    ),
    max: normalizeChartNumericValue(
      datastream.max ?? datastream.maximum ?? datastream.maxValue ?? datastream.max_value
    ),
  };
};

export const getNumericRange = (values = []) => {
  const finiteValues = values
    .map(normalizeChartNumericValue)
    .filter((value) => value !== null);

  if (finiteValues.length === 0) {
    return { min: null, max: null };
  }

  return {
    min: Math.min(...finiteValues),
    max: Math.max(...finiteValues),
  };
};

const TIGHT_NICE_FRACTIONS = [1, 1.1, 1.2, 1.25, 1.5, 2, 2.5, 3, 4, 5, 5.5, 6, 7.5, 10];

const getNiceCeil = (value, fractions = [1, 2, 5, 10]) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue === 0) return numericValue;
  if (numericValue < 0) return -getNiceFloor(Math.abs(numericValue), fractions);

  const exponent = Math.floor(Math.log10(numericValue));
  const base = 10 ** exponent;
  const fraction = numericValue / base;
  const niceFraction = fractions.find((candidate) => fraction <= candidate) || 10;

  return niceFraction * base;
};

const getNiceFloor = (value, fractions = [1, 2, 5, 10]) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue === 0) return numericValue;
  if (numericValue < 0) return -getNiceCeil(Math.abs(numericValue), fractions);

  const exponent = Math.floor(Math.log10(numericValue));
  const base = 10 ** exponent;
  const fraction = numericValue / base;
  let niceFraction = fractions[0] || 1;

  for (const candidate of fractions) {
    if (candidate <= fraction) {
      niceFraction = candidate;
    } else {
      break;
    }
  }

  return niceFraction * base;
};

const clampToBounds = (value, minBound, maxBound) => {
  let nextValue = value;
  if (minBound !== null && nextValue < minBound) nextValue = minBound;
  if (maxBound !== null && nextValue > maxBound) nextValue = maxBound;
  return nextValue;
};

const normalizeBounds = (bounds = {}) => {
  const minBound = normalizeChartNumericValue(bounds.min);
  const maxBound = normalizeChartNumericValue(bounds.max);

  if (minBound !== null && maxBound !== null && maxBound <= minBound) {
    return { min: null, max: null };
  }

  return { min: minBound, max: maxBound };
};

const expandCollapsedDomain = ({ min, max, dataMin, dataMax, minBound, maxBound }) => {
  if (max > min) return { min, max };

  const center = Number.isFinite(dataMin) && Number.isFinite(dataMax)
    ? (dataMin + dataMax) / 2
    : min;
  const boundSpan = minBound !== null && maxBound !== null ? maxBound - minBound : 0;
  const fallbackSpan = Math.max(Math.abs(center) * 0.2, Math.min(boundSpan * 0.02, 1), 1);

  return {
    min: clampToBounds(getNiceFloor(center - fallbackSpan / 2, TIGHT_NICE_FRACTIONS), minBound, maxBound),
    max: clampToBounds(getNiceCeil(center + fallbackSpan / 2, TIGHT_NICE_FRACTIONS), minBound, maxBound),
  };
};

export const resolveStableChartAxisDomain = ({
  dataMin,
  dataMax,
  bounds,
  previousDomain,
  includeZero = false,
} = {}) => {
  const normalizedDataMin = normalizeChartNumericValue(dataMin);
  const normalizedDataMax = normalizeChartNumericValue(dataMax);

  if (normalizedDataMin === null || normalizedDataMax === null) {
    return { min: undefined, max: undefined };
  }

  const { min: minBound, max: maxBound } = normalizeBounds(bounds);
  const requiredMin = includeZero ? Math.min(normalizedDataMin, 0) : normalizedDataMin;
  const requiredMax = includeZero ? Math.max(normalizedDataMax, 0) : normalizedDataMax;
  const previousMin = normalizeChartNumericValue(previousDomain?.min);
  const previousMax = normalizeChartNumericValue(previousDomain?.max);

  if (
    previousMin !== null &&
    previousMax !== null &&
    previousMax > previousMin &&
    requiredMin > previousMin &&
    requiredMax < previousMax
  ) {
    return { min: previousMin, max: previousMax };
  }

  const dataSpan = Math.max(0, requiredMax - requiredMin);
  const lowerPadding = includeZero && normalizedDataMin >= 0
    ? 0
    : Math.max(dataSpan * 0.1, Math.abs(requiredMin) * 0.1, 1);
  const upperPadding = includeZero && normalizedDataMax <= 0
    ? 0
    : Math.max(dataSpan * 0.1, Math.abs(requiredMax) * 0.1, 1);

  let min = getNiceFloor(requiredMin - lowerPadding, TIGHT_NICE_FRACTIONS);
  let max = getNiceCeil(requiredMax + upperPadding, TIGHT_NICE_FRACTIONS);

  min = clampToBounds(min, minBound, maxBound);
  max = clampToBounds(max, minBound, maxBound);

  const expanded = expandCollapsedDomain({
    min,
    max,
    dataMin: requiredMin,
    dataMax: requiredMax,
    minBound,
    maxBound,
  });

  if (expanded.max <= expanded.min) {
    return { min: undefined, max: undefined };
  }

  return expanded;
};
